import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  MapPin, Globe, Linkedin, Instagram, Mail, Star,
  Briefcase, CheckCircle2, ArrowLeft, User, ExternalLink,
  ChevronLeft, ChevronRight, X, Clock, SendHorizonal, Loader2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVAIL_STYLES = {
  available:   { label: 'Müsait',       cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  busy:        { label: 'Meşgul',       cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  unavailable: { label: 'Müsait Değil', cls: 'bg-red-100 text-red-700 border-red-300' },
};

// Job Offer Modal
function JobOfferModal({ profile, onClose }) {
  const { token } = useAuth();
  const [form, setForm] = useState({ title: '', description: '', budget: '', budget_currency: 'USD', deadline: '', location: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) { toast.error('Başlık ve açıklama gerekli'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/freelancer/job-offers/send`, {
        freelancer_org_id: profile.org_id,
        title: form.title,
        description: form.description,
        budget: form.budget ? parseFloat(form.budget) : null,
        budget_currency: form.budget_currency,
        deadline: form.deadline,
        location: form.location,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Teklif gönderildi!');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Teklif gönderilemedi');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-gray-900">İş Teklifi Gönder</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">Alıcı: <span className="font-semibold text-gray-800">{profile.full_name}</span></p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Proje / İş Başlığı *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Örn. İstanbul Fuarı için 3D Stand Tasarımı"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Açıklama *</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Projenizi detaylıca anlatın..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 min-h-[80px]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Bütçe</label>
              <input type="number" value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))}
                placeholder="0"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Para Birimi</label>
              <select value={form.budget_currency} onChange={e => setForm(f => ({ ...f, budget_currency: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none">
                {['USD','EUR','TRY','GBP','AED'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Konum</label>
              <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                placeholder="Örn. İstanbul"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 mb-1 block">Son Tarih</label>
              <input type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="flex-1">İptal</Button>
          <Button onClick={submit} disabled={saving} className="flex-1 bg-red-600 hover:bg-red-700 text-white gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendHorizonal className="w-4 h-4" />}Teklif Gönder
          </Button>
        </div>
      </div>
    </div>
  );
}

// Reviews Section
function ReviewsSection({ slug }) {
  const { token, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ avg_rating: 0, review_count: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ rating: 5, comment: '', project_title: '' });
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const canReview = !!user && !user.org_memberships?.some(m => m.role?.startsWith('FREELANCER'));

  const fetchReviews = useCallback(async (p = 1) => {
    try {
      const res = await fetch(`${API}/public/freelancer/${slug}/reviews?page=${p}&limit=5`);
      if (!res.ok) return;
      const data = await res.json();
      setReviews(data.items || []);
      setStats({ avg_rating: data.avg_rating || 0, review_count: data.review_count || 0 });
      setTotal(data.total || 0);
      setPage(p);
    } catch {}
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { fetchReviews(1); }, [fetchReviews]);

  const submitReview = async () => {
    if (!form.comment.trim()) { toast.error('Yorum yazın'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/freelancer/${slug}/reviews`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Yorumunuz eklendi!');
      setShowForm(false);
      setForm({ rating: 5, comment: '', project_title: '' });
      fetchReviews(1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Yorum eklenemedi'); }
    finally { setSubmitting(false); }
  };

  const totalPages = Math.ceil(total / 5);

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 mt-2">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-white font-bold text-lg">Değerlendirmeler</h2>
          {stats.review_count > 0 && (
            <div className="flex items-center gap-1.5 bg-amber-500/20 border border-amber-500/30 rounded-xl px-3 py-1">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span className="text-amber-400 font-bold text-sm">{stats.avg_rating}</span>
              <span className="text-gray-500 text-xs">({stats.review_count})</span>
            </div>
          )}
        </div>
        {canReview && !showForm && (
          <Button size="sm" onClick={() => setShowForm(true)}
            className="bg-red-600 hover:bg-red-700 text-white text-xs gap-1">
            <Star className="w-3 h-3" />Değerlendir
          </Button>
        )}
      </div>

      {/* Review Form */}
      {showForm && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-5">
          <p className="text-white font-semibold text-sm mb-3">Değerlendirme Yaz</p>
          <div className="mb-3">
            <label className="text-xs text-gray-400 mb-1 block">Puan</label>
            <div className="flex gap-1">
              {[1,2,3,4,5].map(n => (
                <button key={n} onClick={() => setForm(f => ({ ...f, rating: n }))}>
                  <Star className={`w-6 h-6 transition-colors ${n <= form.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />
                </button>
              ))}
            </div>
          </div>
          <div className="mb-3">
            <label className="text-xs text-gray-400 mb-1 block">Proje Adı (opsiyonel)</label>
            <input value={form.project_title} onChange={e => setForm(f => ({ ...f, project_title: e.target.value }))}
              placeholder="Örn. CES Stand Tasarımı"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-red-500" />
          </div>
          <div className="mb-3">
            <label className="text-xs text-gray-400 mb-1 block">Yorum *</label>
            <textarea value={form.comment} onChange={e => setForm(f => ({ ...f, comment: e.target.value }))}
              placeholder="Bu freelancer ile çalışma deneyiminizi paylaşın..."
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-red-500 min-h-[80px]" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)} className="border-gray-600 text-gray-400">İptal</Button>
            <Button size="sm" onClick={submitReview} disabled={submitting} className="bg-red-600 hover:bg-red-700 text-white gap-1">
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : null}Gönder
            </Button>
          </div>
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-red-400" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-8">
          <Star className="w-8 h-8 text-gray-700 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Henüz değerlendirme yok</p>
          {canReview && !showForm && (
            <button onClick={() => setShowForm(true)} className="text-red-400 text-xs mt-2 hover:text-red-300">İlk değerlendirmeyi yaz →</button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map(r => (
            <div key={r.id} className="border-b border-gray-800 pb-4 last:border-0">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-white text-sm font-semibold">{r.reviewer_name}</p>
                  {r.project_title && <p className="text-gray-400 text-xs">{r.project_title}</p>}
                </div>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(n => (
                    <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-600'}`} />
                  ))}
                </div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">{r.comment}</p>
              <p className="text-gray-600 text-xs mt-1">{new Date(r.created_at).toLocaleDateString('tr-TR')}</p>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex gap-2 mt-4 justify-center">
          <button disabled={page === 1} onClick={() => fetchReviews(page - 1)}
            className="text-xs text-gray-400 hover:text-white disabled:opacity-30">← Önceki</button>
          <span className="text-xs text-gray-500">{page}/{totalPages}</span>
          <button disabled={page === totalPages} onClick={() => fetchReviews(page + 1)}
            className="text-xs text-gray-400 hover:text-white disabled:opacity-30">Sonraki →</button>
        </div>
      )}
    </div>
  );
}

// Lightbox
function Lightbox({ images, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft') setIdx(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIdx(i => Math.min(images.length - 1, i + 1));
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [images.length, onClose]);

  return (
    <div className="fixed inset-0 bg-black/95 z-[999] flex flex-col items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white">
        <X className="w-8 h-8" />
      </button>
      <div className="relative max-h-[80vh] flex items-center" onClick={e => e.stopPropagation()}>
        <button onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}
          className="absolute left-2 z-10 text-white/70 hover:text-white disabled:opacity-20">
          <ChevronLeft className="w-10 h-10" />
        </button>
        <img src={images[idx]} alt="" className="max-h-[80vh] max-w-[90vw] object-contain rounded-xl" />
        <button onClick={() => setIdx(i => Math.min(images.length - 1, i + 1))} disabled={idx === images.length - 1}
          className="absolute right-2 z-10 text-white/70 hover:text-white disabled:opacity-20">
          <ChevronRight className="w-10 h-10" />
        </button>
      </div>
      <p className="text-white/60 text-sm mt-3">{idx + 1} / {images.length}</p>
      <div className="flex gap-2 mt-3 flex-wrap justify-center">
        {images.map((img, i) => (
          <img key={i} src={img} alt="" onClick={() => setIdx(i)}
            className={`w-12 h-12 object-cover rounded-lg cursor-pointer border-2 transition-all ${i === idx ? 'border-red-500' : 'border-white/20 opacity-60 hover:opacity-100'}`} />
        ))}
      </div>
    </div>
  );
}

export default function FreelancerPublicProfile() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, images: [], idx: 0 });
  const [jobOfferModal, setJobOfferModal] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/freelancer/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setProfile)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center text-center p-6">
      <div className="w-16 h-16 rounded-2xl bg-red-900/20 border border-red-800 flex items-center justify-center mb-4">
        <User className="w-8 h-8 text-red-400" />
      </div>
      <h2 className="text-xl font-bold text-white mb-2">Profil Bulunamadı</h2>
      <p className="text-gray-400 text-sm mb-6">Bu slug ile eşleşen bir freelancer profili yok.</p>
      <Link to="/find-freelancers" className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" />Freelancer Dizinine Dön
      </Link>
    </div>
  );

  const avStyle = AVAIL_STYLES[profile.availability || 'available'];
  const allPortfolioImages = (profile.portfolio || []).flatMap(item =>
    (item.images || []).map(img => ({ img, title: item.title }))
  );

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Navbar */}
      <header className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur border-b border-gray-800 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" />Geri
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 bg-red-600 rounded-md flex items-center justify-center">
              <span className="text-white font-black text-[10px]">ES</span>
            </div>
            <span className="text-white font-black text-sm">EXPO STAND</span>
          </Link>
          <Link to="/freelancer/signup" className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg transition-colors font-semibold">
            Freelancer Ol
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Hero Card */}
        <div className="bg-gray-900 border border-gray-800 rounded-3xl overflow-hidden">
          {/* Red top bar */}
          <div className="h-2 bg-gradient-to-r from-red-600 to-rose-500" />

          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* Avatar */}
              <div className="relative shrink-0">
                {profile.avatar_url ? (
                  <img src={profile.avatar_url} alt={profile.full_name}
                    className="w-24 h-24 rounded-2xl object-cover border-2 border-red-600/30" />
                ) : (
                  <div className="w-24 h-24 rounded-2xl bg-red-900/20 border-2 border-red-800 flex items-center justify-center">
                    <User className="w-10 h-10 text-red-400" />
                  </div>
                )}
                {profile.is_verified && (
                  <div className="absolute -bottom-2 -right-2 w-7 h-7 bg-emerald-500 rounded-full flex items-center justify-center border-2 border-gray-900">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-white">{profile.full_name}</h1>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-red-400 font-semibold text-sm">{profile.category_name}</span>
                      {profile.is_verified && (
                        <span className="inline-flex items-center gap-1 text-xs bg-emerald-900/30 text-emerald-400 border border-emerald-800 px-2 py-0.5 rounded-full">
                          <CheckCircle2 className="w-3 h-3" />Doğrulandı
                        </span>
                      )}
                    </div>
                  </div>
                  <Badge className={`border ${avStyle.cls} text-xs`}>
                    <Clock className="w-3 h-3 mr-1" />{avStyle.label}
                  </Badge>
                </div>

                {profile.headline && (
                  <p className="text-gray-300 mt-3 text-sm leading-relaxed">{profile.headline}</p>
                )}

                {/* Meta row */}
                <div className="flex flex-wrap gap-4 mt-4">
                  {(profile.city || profile.country) && (
                    <span className="flex items-center gap-1.5 text-gray-400 text-sm">
                      <MapPin className="w-4 h-4 shrink-0 text-red-500" />
                      {[profile.city, profile.country].filter(Boolean).join(', ')}
                    </span>
                  )}
                  {profile.daily_rate && (
                    <span className="flex items-center gap-1.5 text-emerald-400 text-sm font-semibold">
                      {profile.daily_rate} {profile.daily_rate_currency || 'USD'} / gün
                    </span>
                  )}
                  {(profile.languages || []).length > 0 && (
                    <span className="text-gray-400 text-sm">
                      {profile.languages.join(' · ')}
                    </span>
                  )}
                </div>

                {/* Social links */}
                <div className="flex items-center gap-3 mt-4">
                  {profile.website && (
                    <a href={profile.website} target="_blank" rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-white transition-colors">
                      <Globe className="w-4 h-4" />
                    </a>
                  )}
                  {profile.linkedin && (
                    <a href={profile.linkedin} target="_blank" rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-blue-400 transition-colors">
                      <Linkedin className="w-4 h-4" />
                    </a>
                  )}
                  {profile.instagram && (
                    <a href={profile.instagram} target="_blank" rel="noopener noreferrer"
                      className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 hover:text-pink-400 transition-colors">
                      <Instagram className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Bio */}
            {profile.bio && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
                <h2 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
                  <User className="w-5 h-5 text-red-400" />Hakkında
                </h2>
                <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{profile.bio}</p>
              </div>
            )}

            {/* Skills */}
            {(profile.skills || []).length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
                <h2 className="text-white font-bold text-lg mb-4">Yetenekler</h2>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map(s => (
                    <span key={s} className="text-xs px-3 py-1.5 rounded-full bg-red-900/20 text-red-300 border border-red-800/50 font-medium">{s}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Portfolio */}
            {(profile.portfolio || []).length > 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
                <h2 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-red-400" />
                  Portfolyo
                  <span className="text-xs text-gray-500 font-normal ml-1">{profile.portfolio.length} proje</span>
                </h2>
                <div className="space-y-4">
                  {profile.portfolio.map(item => (
                    <div key={item.id} className="border border-gray-800 rounded-xl p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-white text-sm">{item.title}</p>
                          {item.description && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{item.description}</p>}
                          {item.price && (
                            <span className="inline-block mt-1.5 text-xs bg-emerald-900/20 text-emerald-400 border border-emerald-800/50 px-2 py-0.5 rounded-full">
                              {item.price} {item.currency}
                            </span>
                          )}
                        </div>
                      </div>
                      {(item.images || []).length > 0 && (
                        <div className="flex gap-2 flex-wrap">
                          {item.images.map((img, i) => (
                            <div key={i} className="relative group cursor-pointer"
                              onClick={() => {
                                const imgs = item.images;
                                setLightbox({ open: true, images: imgs, idx: i });
                              }}>
                              <img src={img} alt="" className="w-20 h-20 object-cover rounded-lg border border-gray-700 group-hover:border-red-500 transition-colors" />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors flex items-center justify-center">
                                <ExternalLink className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column — sidebar */}
          <div className="space-y-4">
            {/* Contact CTA */}
            <div className="bg-gradient-to-br from-red-900/40 to-rose-900/20 border border-red-800/40 rounded-2xl p-5">
              <h3 className="text-white font-bold mb-2 text-sm">İletişime Geç</h3>
              <p className="text-gray-400 text-xs mb-4 leading-relaxed">
                Bu freelancer ile çalışmak ister misiniz? Platform üzerinden mesaj gönderin.
              </p>
              {user ? (
                <Button onClick={() => setJobOfferModal(true)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white text-sm gap-2">
                  <SendHorizonal className="w-4 h-4" />İş Teklifi Gönder
                </Button>
              ) : (
                <div className="space-y-2">
                  <Link to="/exhibitor/login">
                    <Button variant="outline" className="w-full text-sm border-red-800 text-red-300 hover:bg-red-900/20">
                      Giriş Yap
                    </Button>
                  </Link>
                  <p className="text-gray-600 text-xs text-center">veya</p>
                  <Link to="/freelancer/signup">
                    <Button className="w-full bg-red-600 hover:bg-red-700 text-white text-sm">
                      Freelancer Kaydol
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 space-y-3">
              <h3 className="text-white font-bold text-sm mb-3">Profil Bilgileri</h3>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Kategori</span>
                <span className="text-red-400 font-medium">{profile.category_name}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Portföy</span>
                <span className="text-white font-medium">{(profile.portfolio || []).length} proje</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Durum</span>
                <Badge className={`text-xs border ${avStyle.cls}`}>{avStyle.label}</Badge>
              </div>
              {profile.daily_rate && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Günlük Ücret</span>
                  <span className="text-emerald-400 font-semibold">{profile.daily_rate} {profile.daily_rate_currency}</span>
                </div>
              )}
              {(profile.skills || []).length > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Yetenek</span>
                  <span className="text-white">{profile.skills.length} alan</span>
                </div>
              )}
            </div>

            {/* Find more */}
            <Link to="/find-freelancers"
              className="block bg-gray-900 border border-gray-800 hover:border-red-800 rounded-2xl p-4 text-center text-sm text-gray-400 hover:text-red-400 transition-colors">
              Diğer Freelancer'ları Gör →
            </Link>
          </div>
        </div>

        {/* Reviews Section */}
        <ReviewsSection slug={profile.slug} />
      </div>

      {/* Lightbox */}
      {lightbox.open && (
        <Lightbox images={lightbox.images} startIdx={lightbox.idx} onClose={() => setLightbox({ open: false, images: [], idx: 0 })} />
      )}

      {/* Job Offer Modal */}
      {jobOfferModal && (
        <JobOfferModal profile={profile} onClose={() => setJobOfferModal(false)} />
      )}
    </div>
  );
}
